var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
import { TransportError } from './framework/Transport';
const Response = z.object({
    status: z.object({
        code: z.number(),
        commandResult: z.number(),
        msg: z.string(),
        query: z.string(),
    }),
});
const KeyPressResponse = z.object({
    hold: z.string(),
    key: z.string(),
    status: z.object({
        code: z.number(),
        commandResult: z.number(),
        msg: z.string(),
        query: z.string(),
    }),
});
const GetVersionResponse = z.object({
    DMA: z.number(),
    accessCardId: z.string(),
    isEmployeeAccount: z.boolean(),
    locationName: z.string(),
    manufacturer: z.string(),
    modelId: z.number(),
    receiverId: z.string(),
    status: z.object({
        code: z.number(),
        commandResult: z.number(),
        msg: z.string(),
        query: z.string(),
    }),
    stbSoftwareVersion: z.string(),
    systemTime: z.number(),
    version: z.string(),
});
function isResp(schema, resp) {
    return schema.safeParse(resp).success;
}
export class DirectvNetwork {
    // other class properties and constructor
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        this.config = {
            equipmentId: '',
            equipmentType: DirectvNetwork.key,
            ipAddress: '',
        };
        // controls
        this.keyCodes = {
            back: 'back',
            channelDown: 'chandown',
            channelUp: 'chanup',
            channelPrevious: 'prev',
            directionDown: 'down',
            directionEnter: 'select',
            directionLeft: 'left',
            directionRight: 'right',
            directionUp: 'up',
            exit: 'exit',
            guide: 'guide',
            info: 'info',
            key0: '0',
            key1: '1',
            key2: '2',
            key3: '3',
            key4: '4',
            key5: '5',
            key6: '6',
            key7: '7',
            key8: '8',
            key9: '9',
            keyDash: 'dash',
            keyEnter: 'enter',
            menu: 'menu',
        };
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpGetJson({ cmd: `/info/getVersion` }, GetVersionResponse, tags);
                return (resp === null || resp === void 0 ? void 0 : resp.status.commandResult) === 0
                    ? resp.version
                    : this.state.firmwareVersion.value;
            }),
        };
        this.mediaOperation = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (value.operation === 'channelSet') {
                    const channelHasDash = value.channel.indexOf('-') > -1;
                    let majorChannel = value.channel;
                    let minorChannel = '65535'; // indicates no minor channel number
                    if (channelHasDash) {
                        [majorChannel, minorChannel] = value.channel.split('-');
                    }
                    const resp = yield this.httpGetJson({ cmd: `/tv/tune?major=${majorChannel}&minor=${minorChannel}` }, Response, [...tags, 'channelSet']);
                    if ((resp === null || resp === void 0 ? void 0 : resp.status.commandResult) === 0) {
                        this.state.channel.update({ channel: value.channel });
                    }
                }
                else if (Object.keys(this.keyCodes).includes(value.operation)) {
                    const code = this.keyCodes[value.operation];
                    yield this.httpGetJson({ cmd: `/remote/processKey?key=${code}` }, KeyPressResponse, [...tags, 'keyPress']);
                }
                else {
                    throw new Error(`unhandled media operation: ${value.operation}`);
                }
                return value;
            }),
            powerOffRestrictions: ['set'],
        };
        this.mediaOperations = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const mediaOperations = [
                    'channelPrevious',
                    'channelSet',
                    'channelUpDown',
                    'directionUpDownLeftRight',
                    'directionEnter',
                    'exit',
                    'guide',
                    'info',
                    'keyDash',
                    'keyDigits',
                    'keyEnter',
                    'menu',
                ];
                return Object.fromEntries(Object.values((_a = this.config.videoInputs) !== null && _a !== void 0 ? _a : {}).map(videoInput => [
                    videoInput.code,
                    mediaOperations,
                ]));
            }),
        };
        this.power = {
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpGetJson({ cmd: `/remote/processKey?key=${value ? 'poweron' : 'poweroff'}` }, KeyPressResponse, tags);
                return (resp === null || resp === void 0 ? void 0 : resp.status.commandResult) === 0 ? value : this.state.power.value;
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 10000,
                controls: ['firmwareVersion'],
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.config = config;
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const resp = yield this.httpGetJson({ cmd: `/info/getVersion` }, GetVersionResponse, [
                    'initialize',
                ]);
                if ((resp === null || resp === void 0 ? void 0 : resp.status.commandResult) === 0) {
                    return 'connected';
                }
            }
            catch (error) {
                if (!(error instanceof TransportError && error.code === 'sendTimeout')) {
                    throw error;
                }
            }
            return 'verifyFailed';
        });
    }
    // other class methods
    httpGetJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            if (tags)
                requestOptions.tags = tags;
            requestOptions.serializer = 'utf8';
            requestOptions.headers = {
                Accept: '*/*',
                Connection: 'close',
                Cookie: '', // Set for testing mira connect appliance vs. software behavior
            };
            const resp = yield this.http.requestJson('get', `:8080${cmd.cmd}`, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
}
// static properties
DirectvNetwork.driverType = 'httpDriver';
DirectvNetwork.key = '-NziLf9sDdTxGK_sM_mG';
DirectvNetwork.httpRequestOptions = {
    scheme: 'http',
    idleTimeoutMs: 30000,
};
